import { Typography } from "@circle-react-shared/uikit/Typography";
import { Row as RowSkeleton } from "@circle-react-uikit/SkeletonLoader/components/Row";
import { classNames } from "@circle-react/helpers/twMergeWithCN";

import { t } from "@/i18n-js/instance";
import {
  dateFormats,
  formattedDateTime,
} from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import {
  isTrialThenUpfrontThenValue,
  isTrialThenValue,
} from "@/react/helpers/paywallPriceHelpers";

import { usePricePreviewContext } from "../../PricePreviewProvider";

interface NextPaymentLineProps {
  className?: string;
}

const getPaymentDate = (date: string | undefined): string | undefined => {
  if (!date) return undefined;

  const regex = /(\d\d\d\d)-(\d\d)-(\d\d)/.exec(date);

  if (regex) {
    const [, year, month, day] = regex;
    const dateGenerated = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
    );

    return formattedDateTime({
      dateTime: dateGenerated,
      format: dateFormats.short_month_and_date_with_year,
    });
  }

  return undefined;
};

export const NextPaymentLine = ({ className }: NextPaymentLineProps) => {
  const { previewMutation } = usePricePreviewContext();
  const preview = previewMutation.data;

  if (!preview || previewMutation.isPending) {
    return <RowSkeleton lg className={className} />;
  }

  const nextPaymentLineString = [];

  const hasTax =
    (preview?.first_payment?.tax || 0) > 0 ||
    (preview?.future_payments?.tax || 0) > 0;

  if (hasTax) {
    nextPaymentLineString.push(t("tax_included"));
  }

  if (
    isTrialThenValue(preview.preview_scenario) ||
    isTrialThenUpfrontThenValue(preview.preview_scenario)
  ) {
    const dateString = getPaymentDate(preview.first_payment.due_on);
    if (dateString) {
      nextPaymentLineString.push(
        t("next_payment", {
          next_payment_date: dateString,
        }),
      );
    }
  } else {
    const dateString = getPaymentDate(preview.future_payments?.start_on);
    if (preview.future_payments && dateString) {
      nextPaymentLineString.push(
        t("next_payment", { next_payment_date: dateString }),
      );
    }
  }

  const stringToRender = nextPaymentLineString.join(" ");
  if (stringToRender === "") {
    return null;
  }

  return (
    <div className={classNames("size-full", className)}>
      <Typography.BodyXs color="text-default">
        {stringToRender}
      </Typography.BodyXs>
    </div>
  );
};
